import LecturerLayout from '../../components/LecturerLayout';
import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosInstance';

const TYPE_LEAVE = 'leave';
const TYPE_UNAVAILABILITY = 'unavailability';

const emptyForm = {
    type: TYPE_LEAVE,
    reason: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
};

const formatDate = (value) => {
    if (!value) return 'N/A';
    const d = new Date(value);
    if (isNaN(d)) return value;
    d.setDate(d.getDate() + 1);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (time) => {
    if (!time) return '—';
    const parts = String(time).split(':');
    if (parts.length < 2) return time;
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1] ?? '00';
    const suffix = hours >= 12 ? 'PM' : 'AM';
    if (hours === 0) hours = 12;
    else if (hours > 12) hours -= 12;
    return `${String(hours).padStart(2, '0')}:${minutes} ${suffix}`;
};

export default function Unavailability() {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);
    const [filterType, setFilterType] = useState('all');

    useEffect(() => {
        fetchRecords();
    }, []);

    const fetchRecords = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/lecturer/unavailability');
            setRecords(res.data?.records || res.data || []);
        } catch (e) {
            Swal.fire('Error', e?.response?.data?.message || 'Failed to load records', 'error');
        } finally {
            setLoading(false);
        }
    };

    const openAddForm = () => {
        setFormData(emptyForm);
        setShowForm(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const today = new Date().toISOString().split('T')[0];
        if (formData.date < today) {
            Swal.fire('Validation', 'You cannot add a record for a past date.', 'warning');
            return;
        }
        if (!formData.reason.trim()) {
            Swal.fire('Validation', 'Reason is required.', 'warning');
            return;
        }
        if (formData.type === TYPE_UNAVAILABILITY && (!formData.startTime || !formData.endTime)) {
            Swal.fire('Validation', 'Start time and end time are required for unavailability.', 'warning');
            return;
        }

        const payload = {
            type: formData.type,
            reason: formData.reason.trim(),
            date: formData.date,
            startTime: formData.type === TYPE_UNAVAILABILITY ? formData.startTime : null,
            endTime: formData.type === TYPE_UNAVAILABILITY ? formData.endTime : null,
        };

        setSubmitting(true);
        try {
            await axiosInstance.post('/lecturer/unavailability', payload);
            Swal.fire('Added', 'Record added successfully.', 'success');
            setShowForm(false);
            fetchRecords();
        } catch (e) {
            Swal.fire('Error', e?.response?.data?.message || 'Failed to save record', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const filtered = filterType === 'all' ? records : records.filter((r) => r.type === filterType);

    return (
        <LecturerLayout>
            <div className="p-6 max-w-5xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">My Unavailability</h1>
                        <p className="text-sm text-gray-500 mt-1">Manage your leave and unavailability records</p>
                    </div>
                    <button
                        onClick={openAddForm}
                        className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                    >
                        + Add Record
                    </button>
                </div>

                {/* Filter */}
                <div className="flex gap-2 mb-6">
                    {['all', TYPE_LEAVE, TYPE_UNAVAILABILITY].map((t) => (
                        <button
                            key={t}
                            onClick={() => setFilterType(t)}
                            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                                filterType === t
                                    ? 'bg-emerald-600 text-white border-emerald-600'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-emerald-400'
                            }`}
                        >
                            {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
                        </button>
                    ))}
                </div>

                {/* Table */}
                {loading ? (
                    <div className="text-center py-16 text-gray-400">Loading...</div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">No records found.</div>
                ) : (
                    <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Type</th>
                                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Date</th>
                                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Time</th>
                                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Reason</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((record, idx) => (
                                    <tr key={record.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-4 py-3">
                                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                                                record.type === TYPE_LEAVE
                                                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                                                    : 'bg-rose-50 text-rose-700 border-rose-200'
                                            }`}>
                                                {record.type === TYPE_LEAVE ? 'Leave' : 'Unavailability'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-700">{formatDate(record.date)}</td>
                                        <td className="px-4 py-3 text-gray-600">
                                            {record.type === TYPE_UNAVAILABILITY
                                                ? `${formatTime(record.startTime)} – ${formatTime(record.endTime)}`
                                                : <span className="text-gray-400">Full day</span>}
                                        </td>
                                        <td className="px-4 py-3 text-gray-700 max-w-xs truncate" title={record.reason}>{record.reason}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-6">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="text-lg font-bold text-gray-800">Add Record</h2>
                            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type <span className="text-red-500">*</span></label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                >
                                    <option value={TYPE_LEAVE}>Leave (Full Day)</option>
                                    <option value={TYPE_UNAVAILABILITY}>Unavailability (Partial)</option>
                                </select>
                            </div>

                            {/* Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date <span className="text-red-500">*</span></label>
                                <input
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    min={new Date().toISOString().split('T')[0]}
                                    required
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                />
                            </div>

                            {/* Start / End time — only for unavailability */}
                            {formData.type === TYPE_UNAVAILABILITY && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Time <span className="text-red-500">*</span></label>
                                        <input
                                            type="time"
                                            name="startTime"
                                            value={formData.startTime}
                                            onChange={handleChange}
                                            required
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">End Time <span className="text-red-500">*</span></label>
                                        <input
                                            type="time"
                                            name="endTime"
                                            value={formData.endTime}
                                            onChange={handleChange}
                                            required
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Reason */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
                                <textarea
                                    name="reason"
                                    value={formData.reason}
                                    onChange={handleChange}
                                    rows={3}
                                    placeholder="Enter reason..."
                                    required
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-1">
                                <button
                                    type="button"
                                    onClick={() => setShowForm(false)}
                                    className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-4 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-colors disabled:opacity-60"
                                >
                                    {submitting ? 'Saving...' : 'Add'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </LecturerLayout>
    );
}
